/** Тосты, модальные окна, всплывающие панели и подтверждения. */
import { h, icon } from '../dom.js';

/* ---------- тосты ---------- */

let toastRoot: HTMLElement | null = null;

export function toast(message: string, kind: 'ok' | 'error' | 'info' = 'info', action?: { label: string; href?: string; onClick?: () => void }) {
  if (!toastRoot) {
    toastRoot = h('div', { class: 'toasts', attrs: { 'aria-live': 'polite' } });
    document.body.append(toastRoot);
  }
  const t = h(
    'div',
    { class: ['toast', `toast-${kind}`] },
    icon(kind === 'ok' ? 'check' : kind === 'error' ? 'alert' : 'info', 18),
    h('span', { class: 'toast-text' }, message),
    action
      ? action.href
        ? h('a', { class: 'toast-action', href: action.href, target: '_blank', rel: 'noreferrer' }, action.label)
        : h('button', { class: 'toast-action', type: 'button', onclick: () => action.onClick?.() }, action.label)
      : null,
  );
  toastRoot.append(t);
  requestAnimationFrame(() => t.classList.add('show'));
  const ttl = kind === 'error' ? 7000 : action ? 9000 : 3500;
  setTimeout(() => {
    t.classList.remove('show');
    setTimeout(() => t.remove(), 300);
  }, ttl);
}

/* ---------- модальные окна ---------- */

export interface ModalHandle {
  el: HTMLElement;
  body: HTMLElement;
  footer: HTMLElement;
  close: () => void;
}

export function modal(opts: { title: string; icon?: string; wide?: boolean; onClose?: () => void; subtitle?: string }): ModalHandle {
  const prevFocus = document.activeElement as HTMLElement | null;
  const body = h('div', { class: 'modal-body' });
  const footer = h('div', { class: 'modal-footer' });
  const close = () => {
    overlay.classList.remove('show');
    document.removeEventListener('keydown', onKey);
    setTimeout(() => overlay.remove(), 200);
    opts.onClose?.();
    prevFocus?.focus?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !document.querySelector('.popover')) close();
  };
  const dialog = h(
    'div',
    { class: ['modal', 'glass', opts.wide && 'is-wide'], attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.title } },
    h(
      'div',
      { class: 'modal-head' },
      opts.icon ? h('div', { class: 'modal-icon' }, icon(opts.icon, 20)) : null,
      h('div', { class: 'modal-titles' }, h('h2', { class: 'modal-title' }, opts.title), opts.subtitle ? h('p', { class: 'modal-subtitle' }, opts.subtitle) : null),
      h('button', { class: 'icon-btn', type: 'button', title: 'Закрыть', onclick: close }, icon('x', 18)),
    ),
    body,
    footer,
  );
  const overlay = h('div', { class: 'modal-overlay', onmousedown: (e: MouseEvent) => e.target === overlay && close() }, dialog);
  document.body.append(overlay);
  document.addEventListener('keydown', onKey);
  requestAnimationFrame(() => {
    overlay.classList.add('show');
    (dialog.querySelector('input, textarea, select, button.btn-primary') as HTMLElement | null)?.focus();
  });
  return { el: dialog, body, footer, close };
}

export function confirmDialog(title: string, text: string, okLabel = 'Подтвердить', danger = false): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const m = modal({
      title,
      icon: danger ? 'alert' : 'info',
      onClose: () => {
        if (!done) resolve(false);
      },
    });
    m.body.append(h('p', { class: 'muted' }, text));
    m.footer.append(
      h('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Отмена'),
      h(
        'button',
        {
          class: ['btn', danger ? 'btn-danger' : 'btn-primary'],
          type: 'button',
          onclick: () => {
            done = true;
            resolve(true);
            m.close();
          },
        },
        okLabel,
      ),
    );
  });
}

export function promptDialog(title: string, label: string, value = '', okLabel = 'Сохранить', max = 100): Promise<string | null> {
  return new Promise((resolve) => {
    let done = false;
    const m = modal({ title, icon: 'edit', onClose: () => !done && resolve(null) });
    const input = h('input', { class: 'input', value, maxLength: max, attrs: { 'aria-label': label } });
    const submit = () => {
      if (!input.value.trim()) {
        input.focus();
        return;
      }
      done = true;
      resolve(input.value.trim());
      m.close();
    };
    input.addEventListener('keydown', (e) => e.key === 'Enter' && submit());
    m.body.append(h('label', { class: 'field' }, h('span', { class: 'field-label' }, label), input));
    m.footer.append(h('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Отмена'), h('button', { class: 'btn btn-primary', type: 'button', onclick: submit }, okLabel));
    setTimeout(() => {
      input.focus();
      input.select();
    }, 50);
  });
}

/* ---------- всплывающие панели ---------- */

let openPopover: { el: HTMLElement; close: () => void } | null = null;

export function popover(anchor: HTMLElement, content: HTMLElement, opts: { width?: number; onClose?: () => void } = {}) {
  openPopover?.close();
  const el = h('div', { class: 'popover glass', style: opts.width ? { width: `${opts.width}px` } : undefined }, content);
  document.body.append(el);
  const place = () => {
    const r = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const hgt = el.offsetHeight;
    let left = Math.min(r.left, window.innerWidth - w - 12);
    left = Math.max(12, left);
    let top = r.bottom + 6;
    if (top + hgt > window.innerHeight - 12) top = Math.max(12, r.top - hgt - 6);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  };
  place();
  requestAnimationFrame(() => el.classList.add('show'));
  const onDoc = (e: MouseEvent) => {
    if (!el.contains(e.target as Node) && !anchor.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  const close = () => {
    document.removeEventListener('mousedown', onDoc, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', place);
    el.remove();
    if (openPopover?.el === el) openPopover = null;
    opts.onClose?.();
  };
  setTimeout(() => {
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey, true);
  });
  window.addEventListener('resize', place);
  openPopover = { el, close };
  return { el, close, place };
}

export function closePopover() {
  openPopover?.close();
}
